import React, { useMemo } from 'react';
import DeviceInfo from 'react-native-device-info';
import styled from 'styled-components/native';

import { memo } from '@webtrader/core/constants/react';
import { EDataTestId } from '@webtrader/core/constants/test';
import dynamicSize from '@webtrader/core/dynamic-size';
import { useChart } from '@webtrader/core/services/chart';

import Text from '@/components/Text';
import TouchableOpacity from '@/components/Touch/Opacity';

import Checkmark from '../Checkmark';

export interface IBaseActionSheetOption<T> {
  label: string;
  value: T;
  testID?: EDataTestId;
}

interface IProps<T> {
  value?: T;
  option: IBaseActionSheetOption<T>;
  justifyCenter?: boolean;
  testID?: EDataTestId;
  onSelect: (value: T) => void;
}

const ActionSheetOptionSelector = <T,>(props: IProps<T>) => {
  const { value, option, justifyCenter, testID, onSelect } = props;
  const { label, value: optionValue } = option;
  const { fullscreen } = useChart();

  const selected = optionValue === value;
  const onSelectOption = () => onSelect(optionValue);
  const chartLandscapeMode = DeviceInfo.hasNotch() && fullscreen;

  const SheetOptionCheckMark = useMemo(() => {
    return justifyCenter ? (
      <FloatRightWrapper>
        <Checkmark />
      </FloatRightWrapper>
    ) : (
      <Checkmark />
    );
  }, [justifyCenter]);

  return (
    <SheetOptionItem
      selected={selected}
      landscape={chartLandscapeMode}
      activeOpacity={0.8}
      onPress={onSelectOption}
      testID={testID}
      accessibilityState={{ selected }}
      accessibilityLabel={label}
    >
      {justifyCenter && <SheetOptionItemPrefix />}
      <SheetOptionItemText justifyCenter={justifyCenter}>{label}</SheetOptionItemText>
      {selected && SheetOptionCheckMark}
    </SheetOptionItem>
  );
};

export default memo(ActionSheetOptionSelector);

const SheetOptionItem = styled(TouchableOpacity)<{ selected: boolean; landscape: boolean }>`
  align-items: center;
  flex-direction: row;
  justify-content: space-between;

  padding-top: ${dynamicSize.convertSize(12)}px;
  padding-bottom: ${dynamicSize.convertSize(12)}px;
  padding-end: ${dynamicSize.convertSize(16)}px;
  padding-start: ${({ landscape }) => dynamicSize.convertSize(landscape ? 66 : 16)}px;
  background-color: ${({ selected, theme }) => (selected ? theme.colors.background[1] : theme.colors.background[0])};
`;

const SheetOptionItemText = styled(Text)<{ justifyCenter?: boolean }>`
  ${({ justifyCenter }) => justifyCenter && 'margin: auto;'}
  font-size: ${dynamicSize.convertSize(14)}px;
`;

const SheetOptionItemPrefix = styled.View`
  flex-basis: 1;
`;

const FloatRightWrapper = styled.View`
  position: absolute;
  right: ${dynamicSize.convertSize(16)}px;
`;
